import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Topbar from '../components/Topbar';
import { toast } from 'react-toastify';
import { getDashboard } from '../services/dashboardService';
import Spinner from '../components/Spinner';
import { useRefresh } from '../utils/RefreshContext';

const AnimatedCounter = ({ value, duration = 1500 }) => {
    const [count, setCount] = useState(0);
    const target = parseInt(value?.toString().replace(/,/g, '') || '0', 10);

    useEffect(() => {
        if (target === 0) {
            setCount(0);
            return;
        }

        let start = 0;
        let startTime = null;

        const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = timestamp - startTime;
            const percentage = Math.min(progress / duration, 1);

            // Ease out quad
            const easeProgress = percentage * (2 - percentage);

            setCount(Math.floor(easeProgress * target));

            if (progress < duration) {
                window.requestAnimationFrame(animate);
            } else {
                setCount(target);
            }
        };

        window.requestAnimationFrame(animate);
    }, [target, duration]);

    return <span>{count.toLocaleString()}</span>;
};

const Dashboard = () => {
    const navigate = useNavigate();
    const { refreshKey } = useRefresh();

    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboard = async () => {
            try {
                const response = await getDashboard();
                setDashboardData(response.data);
            } catch (error) {
                console.error('Dashboard API Error:', error);
                // toast.error("Failed to load dashboard data");
            } finally {
                setLoading(false);
            }
        };

        fetchDashboard();
    }, [refreshKey]);

    if (loading) {
        return <Spinner />;
    }

    const d = dashboardData || {};

    const stats = [
        {
            label: 'Total Registered Users',
            value: d.users?.total?.toLocaleString() || '0',
            badge: `↑ ${d.users?.growth || 0}% this month`,
            badgeBg: '#EAFAF3',
            badgeColor: '#1A7A42',
            iconBg: '#EEF6FF',
            iconColor: '#66B2FF',
            icon: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>
        },
        {
            label: 'Active Users Today',
            value: d.users?.activeToday?.toString() || '0',
            sub: 'Last 24 hours',
            iconBg: '#EAFAF3',
            iconColor: '#2ECC71',
            icon: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>
        },
        {
            label: 'New Signups This Week',
            value: d.users?.newThisWeek?.toString() || '0',
            badge: `↑ ${d.users?.todayCount || 0} today`,
            badgeBg: '#FFFBE6',
            badgeColor: '#B8860B',
            iconBg: '#FFFBE6',
            iconColor: '#FFCB2B',
            icon: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></>
        },
        {
            label: 'Total Airports',
            value: d.airports?.total?.toString() || '0',
            subEl: <><span style={{ color: '#2ECC71', fontWeight: 700 }}>{d.airports?.live || 0} Live</span> · {d.airports?.comingSoon || 0} Coming Soon</>,
            iconBg: '#EEF6FF',
            iconColor: '#66B2FF',
            icon: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></>
        },
        {
            label: 'Total POIs (All Airports)',
            value: d.pois?.total?.toString() || '0',
            sub: 'Across all concourses',
            iconBg: '#FFF0F0',
            iconColor: '#FF5C5C',
            icon: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>
        },
        {
            label: 'CMH Total POIs',
            value: d.pois?.cmhTotal?.toString() || '0',
            sub: 'A · B · C · Pre-Security',
            iconBg: '#F5F0FF',
            iconColor: '#8B5CF6',
            icon: <><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></>
        },
    ];

    const recentSignups = d.recentSignups || [];
    const recentActivity = d.recentActivity || [];

    const getFormattedDate = () => {
        const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
        return new Date().toLocaleDateString('en-US', options) + ' — Welcome back';
    };

    return (
        <div>
            <Topbar title="Dashboard" subtitle={getFormattedDate()} />

            <div className="content">

                {/* STAT CARDS */}
                <div className="stats-grid">
                    {stats.map((s, i) => (
                        <div className="stat-card" key={i}>
                            <div className="stat-icon" style={{ background: s.iconBg }}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={s.iconColor} strokeWidth="1.8" strokeLinecap="round">
                                    {s.icon}
                                </svg>
                            </div>
                            <div>
                                <div className="stat-label">{s.label}</div>
                                <div className="stat-value"><AnimatedCounter value={s.value} /></div>
                                {s.badge && <div className="stat-badge" style={{ background: s.badgeBg, color: s.badgeColor }}>{s.badge}</div>}
                                {s.sub && <div className="stat-sub">{s.sub}</div>}
                                {s.subEl && <div className="stat-sub">{s.subEl}</div>}
                            </div>
                        </div>
                    ))}
                </div>

                {/* CMH POI BREAKDOWN */}
                {/* <div className="card" style={{ padding: '22px 24px', marginBottom: 28 }}>
                    <div className="card-title">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="1.8">
                            <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                            <polyline points="2 17 12 22 22 17"/>
                            <polyline points="2 12 12 17 22 12"/>
                        </svg>
                        Total POIs per Concourse — CMH (John Glenn Columbus)
                    </div>
                    <div className="concourse-grid">
                        {concourses.map((c, i) => (
                            <div className="concourse-item" key={i}>
                                <div className="concourse-name">{c.name}</div>
                                <div className="concourse-count">{c.count}</div>
                                <div style={{ fontSize: 12, color: 'var(--grey)', marginTop: 4 }}>POI Points</div>
                                <div className="concourse-bar">
                                    <div className="concourse-fill" style={{ width: c.pct, background: c.color }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div> */}

                {/* QUICK LINKS - Dynamic Navigation */}
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, fontWeight: 700, color: 'var(--navy)', marginBottom: 14 }}>Quick Links</div>
                <div className="quick-links">
                    <div className="quick-link" onClick={() => navigate('/airports')} style={{ cursor: 'pointer' }}>
                        <div className="ql-icon" style={{ background: '#EEF6FF' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#66B2FF" strokeWidth="1.8">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                <circle cx="12" cy="10" r="3" />
                            </svg>
                        </div>
                        <div className="ql-label">Add Airport</div>
                        <div className="ql-sub">New airport entry</div>
                    </div>

                    <div className="quick-link" onClick={() => navigate('/pois')} style={{ cursor: 'pointer' }}>
                        <div className="ql-icon" style={{ background: '#EAFAF3' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2ECC71" strokeWidth="1.8">
                                <path d="M12 5v14M5 12h14" />
                            </svg>
                        </div>
                        <div className="ql-label">Add POI</div>
                        <div className="ql-sub">New point of interest</div>
                    </div>

                    <div className="quick-link" onClick={() => navigate('/users')} style={{ cursor: 'pointer' }}>
                        <div className="ql-icon" style={{ background: '#FFFBE6' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FFCB2B" strokeWidth="1.8">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                            </svg>
                        </div>
                        <div className="ql-label">Manage Users</div>
                        <div className="ql-sub">View all accounts</div>
                    </div>

                    <div
                        className="quick-link"
                        onClick={() => {
                            navigate('/settings?tab=Login History');
                        }}
                    >
                        <div className="ql-icon" style={{ background: '#FFF0F0' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FF5C5C" strokeWidth="1.8">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                            </svg>
                        </div>
                        <div className="ql-label">View Logs</div>
                        <div className="ql-sub">Activity history</div>
                    </div>
                </div>

                {/* BOTTOM GRID */}
                <div className="bottom-grid">

                    {/* RECENT USER SIGNUPS */}
                    <div className="table-card">
                        <div className="table-header">
                            <div className="card-title" style={{ margin: 0 }}>Recent User Signups</div>
                            <div className="view-all" onClick={() => navigate('/users')} style={{ cursor: 'pointer' }}>View All →</div>
                        </div>
                        {recentSignups.length === 0 ? (
                            <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--grey)', fontSize: 15 }}>
                                No recent signups found
                            </div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left', padding: '12px' }}>USER</th>
                                        <th style={{ textAlign: 'left', padding: '12px' }}>TYPE</th>
                                        <th style={{ textAlign: 'left', padding: '12px' }}>DATE</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentSignups.map((user, i) => (
                                        <tr key={i}>
                                            <td style={{ padding: '12px', borderBottom: '1px solid #F0F4F8' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#EEF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#1976D2' }}>
                                                        {user.full_name ? user.full_name.charAt(0).toUpperCase() : 'U'}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 600 }}>{user.full_name}</div>
                                                        <div style={{ fontSize: 13, color: 'var(--grey)' }}>{user.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ padding: '12px', borderBottom: '1px solid #F0F4F8' }}>
                                                <span className={`badge badge-${user.type ? user.type.toLowerCase() : 'free'}`}>{user.type || 'Free'}</span>
                                            </td>
                                            <td style={{ padding: '12px', fontSize: 13, color: 'var(--grey)', borderBottom: '1px solid #F0F4F8' }}>
                                                {new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* RECENT ACTIVITY LOG */}
                    <div className="table-card">
                        <div
                            className="table-header"
                            onClick={() => {
                                navigate('/settings?tab=Login History');
                            }}
                        >
                            <div className="card-title" style={{ margin: 0 }}>Recent Activity Log</div>
                            <div className="view-all">View All →</div>
                        </div>
                        {recentActivity.length === 0 ? (
                            <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--grey)', fontSize: 15 }}>
                                No recent activity found
                            </div>
                        ) : (
                            <div className="activity-list" style={{ padding: '0 20px 20px' }}>
                                {recentActivity.map((activity, i) => (
                                    <div key={activity.id || i} style={{ display: 'flex', gap: 14, padding: '16px 0', borderBottom: i === recentActivity.length - 1 ? 'none' : '1px solid #F0F4F8' }}>
                                        <div style={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            background: activity.result?.includes('Success') || activity.result?.includes('✓') ? '#2ECC71' : '#FF5C5C',
                                            marginTop: 6,
                                            flexShrink: 0,
                                            boxShadow: `0 0 0 3px ${activity.result?.includes('Success') || activity.result?.includes('✓') ? 'rgba(46, 204, 113, 0.1)' : 'rgba(255, 92, 92, 0.1)'}`
                                        }} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)', display: 'flex', justifyContent: 'space-between' }}>
                                                <span>{activity.action}</span>
                                                <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--grey)' }}>{new Date(activity.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                            </div>
                                            <div style={{ fontSize: 13, color: 'var(--grey)', marginTop: 2 }}>
                                                Performed by <span style={{ fontWeight: 500, color: 'var(--dark)' }}>{activity.user?.full_name || 'System'}</span>
                                            </div>
                                            <div style={{ fontSize: 11, color: 'var(--grey)', marginTop: 6, display: 'flex', gap: 10, fontFamily: "'Space Grotesk', sans-serif" }}>
                                                <span>IP: {activity.ip_address || 'Internal'}</span>
                                                <span>•</span>
                                                <span style={{ color: activity.result?.includes('Success') || activity.result?.includes('✓') ? '#27AE60' : '#E74C3C' }}>{activity.result}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                </div>

            </div>
        </div>
    );
};

export default Dashboard;